/**
 * What unfolds under one year of the report table.
 *
 * The summary row carries the six figures that fit a table; the report holds
 * about a dozen more, EMTA holds that year's quarters, and the register holds
 * the turnover split by activity. None of it had anywhere to live before, so it
 * was simply not shown. Everything here is data the mirror already had.
 */
import { BarChart3, CalendarDays, FileCheck2, Layers, PieChart } from "lucide-react";
import { formatEurFull, formatEurShort } from "../../../../lib/ariregister";
import { fmtDate } from "../detailCard";
import { ActivityBars } from "./ActivityBars";
import { MoneyBars } from "./MoneyBars";
import type { ExtraReportField, T, YearDetail } from "./lib";

/** Report field to message key. Adding a field to EXTRA_REPORT_FIELDS means adding it here. */
const FIGURE_KEY: Record<ExtraReportField, string> = {
  profitBeforeTaxCents: "ariregister.financial.fProfitBeforeTax",
  laborCostCents: "ariregister.financial.fLaborCost",
  currentAssetsCents: "ariregister.financial.fCurrentAssets",
  cashCents: "ariregister.financial.fCash",
  currentLiabilitiesCents: "ariregister.financial.fCurrentLiabilities",
  nonCurrentLiabilitiesCents: "ariregister.financial.fNonCurrentLiabilities",
};

function Fact({ icon: Icon, children }: { icon: typeof CalendarDays; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-background/70 px-2 py-0.5 text-[11px] text-muted-foreground">
      <Icon className="size-3" /> {children}
    </span>
  );
}

export function YearPanel({ detail, t }: { detail: YearDetail; t: T }) {
  const { quarters, activities } = detail;
  const facts =
    detail.periodEnd != null ||
    detail.submittedAt != null ||
    detail.category != null ||
    detail.isConsolidated;

  return (
    <div className="rounded-lg border border-border/70 bg-background/50 p-3">
      {facts && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {detail.periodEnd && (
            <Fact icon={CalendarDays}>
              {t("ariregister.financial.periodEnd")}: {fmtDate(detail.periodEnd)}
            </Fact>
          )}
          {detail.submittedAt && (
            <Fact icon={FileCheck2}>
              {t("ariregister.financial.submittedAt")}: {fmtDate(detail.submittedAt)}
            </Fact>
          )}
          {detail.category && <Fact icon={Layers}>{detail.category}</Fact>}
          {detail.isConsolidated && <Fact icon={Layers}>{t("ariregister.financial.consolidated")}</Fact>}
        </div>
      )}

      {detail.figures.length > 0 && (
        <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
          {detail.figures.map((f) => (
            <div key={f.field} className="flex items-baseline justify-between gap-2 border-b border-border/40 pb-1">
              <dt className="min-w-0 text-xs text-muted-foreground">{t(FIGURE_KEY[f.field])}</dt>
              {/* The amount never wraps; the label is the part allowed to break. */}
              <dd className="shrink-0 whitespace-nowrap text-sm tabular-nums" title={formatEurFull(f.cents)}>
                {formatEurShort(f.cents)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {(quarters || activities.length > 0) && (
        <div className={quarters && activities.length > 0 ? "grid gap-4 sm:grid-cols-2" : undefined}>
          {quarters && (
            <MoneyBars
              title={t(
                quarters.metric === "revenue"
                  ? "ariregister.financial.quarters"
                  : "ariregister.financial.quartersTax",
                { year: quarters.year },
              )}
              points={quarters.points}
              seriesLabel={t(
                quarters.metric === "revenue"
                  ? "ariregister.financial.turnover"
                  : "ariregister.financial.stateTax",
              )}
              color="var(--color-primary-2)"
              height={130}
              maxBarSize={48}
              icon={BarChart3}
            />
          )}
          {activities.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <PieChart className="size-3.5" />
                {t("ariregister.financial.revenueByActivity", { year: detail.year })}
              </div>
              <ActivityBars rows={activities} t={t} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
